import { Component, effect, HostListener, viewChild, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { MenuItem, PrimeTemplate } from '@openng/optimus-ui/api';
import { ContextMenu } from '@openng/optimus-ui/contextmenu';
import { Listbox, ListboxChangeEvent } from '@openng/optimus-ui/listbox';
import {
    InputFulltextComponent,
    IOption,
} from '../../../../shared/components/inputs/input-fulltext/input-fulltext.component';
import { FolderCarePlanPageStore } from './folder-care-plan-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { FilterBuilder } from '../../../helper/filter.builder';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CarePlanObject } from '@libs/types/src/services/object-type/folder/care-plan.object';
import { fb } from '../../../../shared/shared.global-service';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { isInvalid } from '../../../../shared/helpers/validate';
import { contentGlobalSuccess } from '../../../content.error';
import { BaseServicePrintDialogComponent } from '../../../../../components/medical-service/print/base-service-print-dialog/base-service-print-dialog.component';
import { folderCarePlanFrag } from './folder-care-plan-page.query';
import { generateFilename } from '../../../../../utils/files.utils';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { formAddText } from '../../../../../utils/form.utils';
import { ServicePatternWysiwygComponent } from '../../../components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { InputWysiwygComponent } from '../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { UserChipComponent } from '../../../../../components/kit/user-chip/user-chip.component';
import { FormsModule } from '@angular/forms';
import { wysiwygPatterGroupsMedicalFolder } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import {
    BackgroundActionType,
    ClientDocumentExportKind,
} from '@libs/types/src/notifications/types/background-action.types';

@Component({
    selector: 'app-folder-care-plan-page',
    templateUrl: './folder-care-plan-page.component.html',
    styleUrls: ['./folder-care-plan-page.component.scss'],
    providers: [FolderCarePlanPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        InputFulltextComponent,
        ButtonComponent,
        ServicePatternWysiwygComponent,
        InputWysiwygComponent,
        Listbox,
        TranslatePipe,
        PrimeTemplate,
        UserChipComponent,
        ContextMenu,
        FormsModule,
    ],
})
export class FolderCarePlanPageComponent extends SubscriptionComponent {
    protected readonly store = inject(FolderCarePlanPageStore);
    protected readonly dialogService = inject(BaseDialogService);
    private readonly translate = inject(TranslateService);
    private readonly backgroundTaskLauncher = inject(
        BackgroundTaskLaunchService,
    );

    protected selectedCarePlan: CarePlanObject | null = null;

    public readonly wysiwygData =
        viewChild<ServicePatternWysiwygComponent>('wysiwygData');
    public readonly wysiwygSymptoms =
        viewChild<InputWysiwygComponent>('wysiwygSymptoms');

    protected form = fb.group({
        search: fb.control<string>(''),
        selectedCarePlanData: fb.control<string>('', { nonNullable: true }),
        selectedCarePlanSymptoms: fb.control<string>('', { nonNullable: true }),
    });

    @ViewChild('contextMenu') ctx!: ContextMenu;
    protected contextMenuItemsAll: MenuItem[] = [
        {
            label: 'update',
            icon: 'pi pi-file-edit',
            command: () => {
                this.update();
            },
        },
        {
            label: 'delete',
            icon: 'pi pi-times',
            command: () => {
                this.delete();
            },
        },
    ];
    protected contextMenuItems = this.contextMenuItemsAll;
    private contextItem: number = 0;

    constructor() {
        super();
        this.store.onSuccess(
            () => this.fetchData(),
            'createCarePlan',
            'updateCarePlan',
            'deleteCarePlan',
        );

        effect(() => {
            const carePlans = this.store.carePlans();

            if (carePlans.length > 0) {
                this.selectedCarePlan = carePlans[0];
                if (this.selectedCarePlan) {
                    this.form.patchValue({
                        selectedCarePlanData: this.selectedCarePlan['data'],
                        selectedCarePlanSymptoms:
                            this.selectedCarePlan['symptoms'],
                    });
                } else {
                    this.form.reset();
                }
            }
        });

        effect(() => {
            if (this.store.folder() && this.store.carePlanSharingIds()) {
                this.fetchData();
            }
        });
        this.contextMenuItemsAll.forEach((it) => {
            it.label = this.translate.instant(
                `database.persons.epicrisis.${it.label}`,
            );
        });
    }

    public listChange(index: ListboxChangeEvent): void {
        if (index.value) {
            this.selectedCarePlan = this.store.carePlans()[index.value.index];
            this.form.patchValue({
                selectedCarePlanData: this.selectedCarePlan?.['data'] || '',
                selectedCarePlanSymptoms:
                    this.selectedCarePlan?.['symptoms'] || '',
            });
        }
    }

    protected async save() {
        if (isInvalid(this.form)) return;

        if (!this.store.folder()) return;
        await this.store.api.createCarePlan({
            folderId: this.store.folder()?.id || 0,
            createdAt: new Date().toISOString(),
            data: this.form.value.selectedCarePlanData || '',
            symptoms: this.form.value.selectedCarePlanSymptoms || '',
        });

        // reset wysiwyg's storage values
        this.wysiwygData()?.resetBackupValues();
        this.wysiwygSymptoms()?.resetBackupValues();

        contentGlobalSuccess.next('common.created');
    }

    protected selectedCarePlanChange(selectedSearch: IOption | null): void {
        this.selectedCarePlan = selectedSearch as any as CarePlanObject;
        this.form.patchValue({
            selectedCarePlanData: selectedSearch?.data ?? '',
            selectedCarePlanSymptoms: (selectedSearch as any)?.symptoms ?? '',
        });
    }

    protected async delete() {
        if (this.contextItem) {
            await this.store.api.deleteCarePlan({ id: this.contextItem });
            contentGlobalSuccess.next('common.deleted');
        }
    }

    protected async update() {
        if (this.contextItem) {
            await this.store.api.updateCarePlan({
                id: this.contextItem,
                data: this.form.value.selectedCarePlanData || '',
                symptoms: this.form.value.selectedCarePlanSymptoms || '',
            });

            // reset wysiwyg's storage values
            this.wysiwygData()?.resetBackupValues();
            this.wysiwygSymptoms()?.resetBackupValues();

            contentGlobalSuccess.next('common.updated');
        }
    }

    protected print() {
        this.dialogService.open(BaseServicePrintDialogComponent, {
            header: this.translate.instant('common.print'),
            data: {
                serviceId: this.store.serviceId(),
                onStart: (value: any) =>
                    this.backgroundTaskLauncher.start({
                        type: BackgroundActionType.CLIENT_DOCUMENT_EXPORT,
                        payload: {
                            folderId: this.store.folderId() || 0,
                            kind: ClientDocumentExportKind.CARE_PLAN,
                            input: {
                                filename: generateFilename(
                                    'folder.carePlan.label',
                                ),
                                id: this.selectedCarePlan?.id || 0,
                                headerId: value.headerId || 0,
                                footerId: value.footerId || 0,
                                archive: value.archive || false,
                            },
                        },
                        alreadyRunningMessage:
                            'person.documentExport.backgroundTask.messages.alreadyRunning',
                        failedMessage:
                            'person.documentExport.backgroundTask.messages.startFailed',
                    }),
            },
        });
    }

    @HostListener('document:contextmenu')
    rightClickAny() {
        this.ctx.hide();
    }

    @HostListener('document:click')
    leftClickAny() {
        this.ctx.hide();
    }

    protected rightClick(
        menu: ContextMenu,
        event: MouseEvent,
        item: CarePlanObject,
    ) {
        this.contextItem = item.id;
        menu.hide();
        event.preventDefault();
        this.contextMenuItems = this.contextMenuItemsAll.slice(
            this.selectedCarePlan?.id === item.id ? 0 : 1,
        );
        setTimeout(() => {
            menu.toggle(event);
        }, 1);
    }

    private fetchData() {
        if (!this.store.folderId() || !this.store.carePlanSharingIds()) return;
        this.store.api
            .getCarePlans(
                FilterBuilder.singlePage({
                    folderId: [
                        this.store.folderId(),
                        ...this.store.carePlanSharingIds()!,
                    ],
                }),
                folderCarePlanFrag,
            )
            .then();
    }

    protected readonly servicePatternUsageEnum =
        ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly wysiwygPatterGroupsMedicalFolder =
        wysiwygPatterGroupsMedicalFolder;
}
